/**
 * Admin: modul Meniu — link extern + galerie (max 15 imagini).
 */
import { $ } from '../utils/helpers.js?v=82.86';
import { showSnackbar } from '../utils/ui.js?v=82.86';
import { sanitizeImageSrc } from '../utils/url.js?v=82.86';
import { validateFileUpload, validateMenuUrl } from '../utils/validation.js?v=82.86';
import { getCurrentUser } from '../services/auth.js?v=82.86';
import { getCurrentSettings, setCurrentSettings } from '../services/settings.js?v=82.86';
import { saveCampaignSettingsBackend } from '../services/backend.js?v=82.86';
import { uploadFile } from '../services/storage.js?v=82.86';
import { refreshAdminSummaries } from './admin.js?v=82.86';
import { logger } from '../utils/logger.js?v=82.86';

export const DEFAULT_MENU_LABEL = 'Meniu';
export const MENU_GALLERY_MAX = 15;

let localMenuGalleryImages = [];
let hasUnsavedMenuModuleChanges = false;
let menuGalleryReplaceIndex = null;

function setMenuModuleDirtyState(dirty) {
  hasUnsavedMenuModuleChanges = !!dirty;
  const badge = document.getElementById('menuModuleDirtyBadge');
  if (badge) badge.style.display = hasUnsavedMenuModuleChanges ? '' : 'none';
}

export function resetLocalMenuGalleryFromSettings() {
  const urls = Array.isArray(getCurrentSettings()?.menuImages)
    ? getCurrentSettings().menuImages
    : [];
  localMenuGalleryImages = urls
    .map((u) => String(u || '').trim())
    .filter(Boolean)
    .slice(0, MENU_GALLERY_MAX)
    .map((url) => ({ type: 'remote', url }));
  setMenuModuleDirtyState(false);
}

function addFilesToGallery(files) {
  if (!files?.length) return;
  for (const file of files) {
    if (localMenuGalleryImages.length >= MENU_GALLERY_MAX) {
      showSnackbar(`Maxim ${MENU_GALLERY_MAX} imagini.`, 'info');
      break;
    }
    const fileValidation = validateFileUpload(file, {
      maxSize: 10 * 1024 * 1024,
      allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
      fieldName: 'Imagine meniu'
    });
    if (!fileValidation.valid) {
      showSnackbar(fileValidation.message, 'error');
      continue;
    }
    const previewUrl = URL.createObjectURL(file);
    localMenuGalleryImages.push({ type: 'local', file, previewUrl, url: previewUrl });
    setMenuModuleDirtyState(true);
  }
  renderMenuGalleryAdminGrid();
}

/** PDF → pagini (poze) în galeria meniului. Un PDF nou înlocuiește pozele de acum (e tot meniul). */
async function importMenuPdf(file, btn) {
  const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name || '');
  if (!isPdf) {
    showSnackbar('Alege un fișier PDF.', 'error');
    return;
  }
  if (file.size > 30 * 1024 * 1024) {
    showSnackbar('PDF-ul e prea mare (maxim 30 MB).', 'error');
    return;
  }
  const label = btn?.textContent;
  if (btn) { btn.disabled = true; btn.textContent = 'Se deschide PDF-ul…'; }
  try {
    const { pdfToImageFiles } = await import('../utils/pdf-to-images.js?v=82.86');
    const { files, totalPages } = await pdfToImageFiles(file, {
      maxPages: MENU_GALLERY_MAX,
      onProgress: (done, total) => { if (btn) btn.textContent = `Pagina ${done} din ${total}…`; }
    });
    if (!files.length) throw new Error('PDF-ul nu are pagini.');
    localMenuGalleryImages.forEach((it) => {
      if (it.type === 'local' && it.previewUrl) {
        try { URL.revokeObjectURL(it.previewUrl); } catch (_) { /* ignore */ }
      }
    });
    localMenuGalleryImages = [];
    addFilesToGallery(files);
    setMenuModuleDirtyState(true);
    showSnackbar(
      totalPages > files.length
        ? `Am pus primele ${files.length} pagini din ${totalPages}. Apasă Salvează.`
        : `${files.length} ${files.length === 1 ? 'pagină pregătită' : 'pagini pregătite'}. Apasă Salvează.`,
      'success'
    );
  } catch (e) {
    logger.warn('[menu] pdf import', e);
    showSnackbar(/password/i.test(e?.name || e?.message || '') ? 'PDF-ul are parolă. Scoate parola și încearcă din nou.' : 'Nu am putut citi PDF-ul. Încearcă alt fișier.', 'error');
  } finally {
    if (btn) { btn.disabled = false; btn.textContent = label; }
  }
}

let menuMode = 'link';

function setMenuMode(mode, { dirty = true } = {}) {
  menuMode = mode === 'images' ? 'images' : 'link';
  document.querySelectorAll('[data-menu-mode]').forEach((b) => {
    const on = b.dataset.menuMode === menuMode;
    b.classList.toggle('is-on', on);
    b.setAttribute('aria-checked', on ? 'true' : 'false');
  });
  const linkField = document.getElementById('menuLinkField');
  const galField = document.getElementById('menuGalleryField');
  if (linkField) linkField.hidden = menuMode !== 'link';
  if (galField) galField.hidden = menuMode !== 'images';
  updateMenuModeNote();
  if (dirty) setMenuModuleDirtyState(true);
}

/** Avertizează când varianta nealeasă are conținut care se va șterge la salvare. */
function updateMenuModeNote() {
  const note = document.getElementById('menuModeNote');
  if (!note) return;
  const url = String($('menuUrl')?.value || '').trim();
  const n = localMenuGalleryImages.length;
  let text = '';
  if (menuMode === 'link' && n > 0) text = `Ai ${n} ${n === 1 ? 'poză' : 'poze'} în galerie. La salvare se șterg — clientul vede doar linkul.`;
  if (menuMode === 'images' && url) text = 'Linkul de meniu se șterge la salvare — clientul vede doar pozele.';
  note.textContent = text;
  note.hidden = !text;
}

export function renderMenuGalleryAdminGrid() {
  const grid = document.getElementById('menuGalleryGrid');
  const countEl = document.getElementById('menuGalleryCount');
  if (!grid) return;
  grid.replaceChildren();

  const n = localMenuGalleryImages.length;
  if (countEl) countEl.textContent = `${n} / ${MENU_GALLERY_MAX}`;
  const addBtn = document.getElementById('menuGalleryAddBtn');
  if (addBtn) addBtn.disabled = n >= MENU_GALLERY_MAX;
  updateMenuModeNote();

  if (n === 0) {
    const empty = document.createElement('p');
    empty.className = 'smallnote';
    empty.textContent = 'Nicio poză încă. Apasă „Adaugă imagini”.';
    grid.appendChild(empty);
    return;
  }

  const mkBtn = (text, label, cls = 'btn-ghost') => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = `${cls} client-promo-gallery-admin-item__btn`;
    b.textContent = text;
    b.title = label;
    b.setAttribute('aria-label', label);
    return b;
  };
  const move = (from, to) => {
    if (to < 0 || to >= localMenuGalleryImages.length) return;
    [localMenuGalleryImages[from], localMenuGalleryImages[to]] = [localMenuGalleryImages[to], localMenuGalleryImages[from]];
    setMenuModuleDirtyState(true);
    renderMenuGalleryAdminGrid();
  };

  localMenuGalleryImages.forEach((item, idx) => {
    const row = document.createElement('div');
    row.className = 'client-promo-gallery-admin-item';
    row.setAttribute('role', 'listitem');
    const img = document.createElement('img');
    img.className = 'client-promo-gallery-admin-item__thumb';
    img.alt = `Pagina ${idx + 1}`;
    img.decoding = 'async';
    const safe = sanitizeImageSrc(item?.url || '');
    if (safe) img.src = safe;
    row.appendChild(img);

    const actions = document.createElement('div');
    actions.className = 'client-promo-gallery-admin-item__actions';
    const up = mkBtn('↑', 'Mută mai sus');
    up.disabled = idx === 0;
    up.addEventListener('click', () => move(idx, idx - 1));
    const down = mkBtn('↓', 'Mută mai jos');
    down.disabled = idx >= localMenuGalleryImages.length - 1;
    down.addEventListener('click', () => move(idx, idx + 1));
    const rep = mkBtn('⟳', 'Înlocuiește poza');
    rep.addEventListener('click', () => {
      menuGalleryReplaceIndex = idx;
      $('menuGalleryUpload')?.click();
    });
    const del = mkBtn('🗑', 'Șterge poza', 'btn-danger client-promo-gallery-admin-item__btn--del');
    let t = null;
    del.addEventListener('click', () => {
      if (!del.classList.contains('is-confirming')) {
        del.classList.add('is-confirming');
        del.textContent = 'Sigur?';
        t = setTimeout(() => { del.classList.remove('is-confirming'); del.textContent = '🗑'; }, 3000);
        return;
      }
      clearTimeout(t);
      const removed = localMenuGalleryImages[idx];
      if (removed?.type === 'local' && removed.previewUrl) {
        try { URL.revokeObjectURL(removed.previewUrl); } catch (_) { /* ignore */ }
      }
      localMenuGalleryImages.splice(idx, 1);
      setMenuModuleDirtyState(true);
      renderMenuGalleryAdminGrid();
    });
    actions.append(up, down, rep, del);
    row.appendChild(actions);
    grid.appendChild(row);
  });
}

function bindMenuModuleAdminControls() {
  const enabledEl = $('menuEnabled');
  if (enabledEl && !enabledEl.dataset.menuToggleBound) {
    enabledEl.dataset.menuToggleBound = '1';
    enabledEl.addEventListener('change', () => refreshAdminSummaries());
  }

  const labelEl = $('menuLabel');
  if (labelEl && !labelEl.dataset.menuLabelBound) {
    labelEl.dataset.menuLabelBound = '1';
    labelEl.addEventListener('input', () => setMenuModuleDirtyState(true));
  }

  const urlEl = $('menuUrl');
  if (urlEl && !urlEl.dataset.menuUrlBound) {
    urlEl.dataset.menuUrlBound = '1';
    urlEl.addEventListener('input', () => { setMenuModuleDirtyState(true); updateMenuModeNote(); });
  }

  document.querySelectorAll('[data-menu-mode]').forEach((b) => {
    if (b.dataset.menuModeBound) return;
    b.dataset.menuModeBound = '1';
    b.addEventListener('click', () => { if (b.dataset.menuMode !== menuMode) setMenuMode(b.dataset.menuMode); });
  });

  const uploadEl = $('menuGalleryUpload');
  const dropzone = document.getElementById('menuGalleryDropzone');
  const addBtn = document.getElementById('menuGalleryAddBtn');

  if (addBtn && uploadEl && !addBtn.dataset.menuAddBound) {
    addBtn.dataset.menuAddBound = '1';
    addBtn.addEventListener('click', () => {
      menuGalleryReplaceIndex = null;
      uploadEl.click();
    });
  }

  const pdfEl = $('menuPdfUpload');
  const pdfBtn = document.getElementById('menuPdfAddBtn');
  if (pdfBtn && pdfEl && !pdfBtn.dataset.menuPdfBound) {
    pdfBtn.dataset.menuPdfBound = '1';
    pdfBtn.addEventListener('click', () => pdfEl.click());
    pdfEl.addEventListener('change', () => {
      const file = (pdfEl.files || [])[0];
      pdfEl.value = '';
      if (file) importMenuPdf(file, pdfBtn);
    });
  }

  if (uploadEl && !uploadEl.dataset.menuUploadBound) {
    uploadEl.dataset.menuUploadBound = '1';
    uploadEl.addEventListener('change', () => {
      const files = Array.from(uploadEl.files || []);
      uploadEl.value = '';
      if (menuGalleryReplaceIndex != null && files[0]) {
        const idx = menuGalleryReplaceIndex;
        menuGalleryReplaceIndex = null;
        const old = localMenuGalleryImages[idx];
        if (old?.type === 'local' && old.previewUrl) {
          try {
            URL.revokeObjectURL(old.previewUrl);
          } catch (_) {
            /* ignore */
          }
        }
        const file = files[0];
        const v = validateFileUpload(file, {
          maxSize: 10 * 1024 * 1024,
          allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
          fieldName: 'Imagine meniu'
        });
        if (!v.valid) {
          showSnackbar(v.message, 'error');
          return;
        }
        const previewUrl = URL.createObjectURL(file);
        localMenuGalleryImages[idx] = { type: 'local', file, previewUrl, url: previewUrl };
        setMenuModuleDirtyState(true);
        renderMenuGalleryAdminGrid();
        return;
      }
      addFilesToGallery(files);
    });
  }

  if (dropzone && !dropzone.dataset.menuDropBound) {
    dropzone.dataset.menuDropBound = '1';
    ['dragenter', 'dragover'].forEach((ev) => {
      dropzone.addEventListener(ev, (e) => {
        e.preventDefault();
        e.stopPropagation();
        dropzone.classList.add('is-dragover');
      });
    });
    ['dragleave', 'drop'].forEach((ev) => {
      dropzone.addEventListener(ev, (e) => {
        e.preventDefault();
        e.stopPropagation();
        dropzone.classList.remove('is-dragover');
      });
    });
    dropzone.addEventListener('drop', (e) => {
      const files = Array.from(e.dataTransfer?.files || []).filter((f) =>
        f.type.startsWith('image/')
      );
      addFilesToGallery(files);
    });
    dropzone.addEventListener('click', () => {
      menuGalleryReplaceIndex = null;
      uploadEl?.click();
    });
  }
}

export function hydrateMenuModuleAdminFromSettings(settings) {
  const s = settings || getCurrentSettings() || {};
  if ($('menuLabel')) {
    const label = String(s.menuLabel || '').trim();
    $('menuLabel').value = label || DEFAULT_MENU_LABEL;
  }
  if ($('menuUrl')) {
    $('menuUrl').value = (s.menuUrl || '').trim();
  }
  if ($('menuEnabled')) {
    $('menuEnabled').checked = s.menuEnabled !== false;
  }
  resetLocalMenuGalleryFromSettings();
  // linkul are prioritate pe pagina publică → dacă există, fereastra se deschide pe „Link”
  const hasUrl = !!String(s.menuUrl || '').trim();
  setMenuMode(hasUrl || !localMenuGalleryImages.length ? 'link' : 'images', { dirty: false });
  renderMenuGalleryAdminGrid();
  bindMenuModuleAdminControls();
}

export async function buildMenuModulePayload(uid) {
  const menuLabel =
    String($('menuLabel')?.value || '')
      .trim()
      .slice(0, 40) || DEFAULT_MENU_LABEL;
  const useLink = menuMode === 'link';
  const menuUrlValidation = useLink ? validateMenuUrl($('menuUrl')?.value) : { valid: true, value: '' };
  if (!menuUrlValidation.valid) throw new Error(menuUrlValidation.message);
  const menuEnabled = $('menuEnabled')?.checked !== false;

  if (localMenuGalleryImages.length > MENU_GALLERY_MAX) {
    throw new Error(`Poți salva maxim ${MENU_GALLERY_MAX} imagini.`);
  }

  const menuImages = [];
  for (const item of (useLink ? [] : localMenuGalleryImages)) {
    if (!item) continue;
    if (item.type === 'local' && item.file) {
      const validation = validateFileUpload(item.file, {
        maxSize: 10 * 1024 * 1024,
        allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
        fieldName: 'Imagine meniu'
      });
      if (!validation.valid) throw new Error(validation.message);
      const uploadedUrl = await uploadFile(uid, 'menu-gallery', item.file);
      if (uploadedUrl) menuImages.push(uploadedUrl);
    } else if (item.type === 'remote' && item.url) {
      menuImages.push(item.url);
    }
  }

  return {
    menuLabel,
    menuUrl: menuUrlValidation.value,
    menuEnabled,
    menuImages,
    menuQrURL: null
  };
}

export async function saveMenuModuleSettings(options = {}) {
  const { quiet = false } = options;
  const currentUser = getCurrentUser();
  if (!currentUser) throw new Error('Nu ești logat');
  const uid = currentUser.uid;

  const toSave = await buildMenuModulePayload(uid);

  await saveCampaignSettingsBackend(toSave);

  const currentSettings = getCurrentSettings();
  if (currentSettings) {
    currentSettings.menuLabel = toSave.menuLabel;
    currentSettings.menuUrl = toSave.menuUrl;
    currentSettings.menuEnabled = toSave.menuEnabled;
    currentSettings.menuImages = toSave.menuImages;
    currentSettings.menuQrURL = null;
    setCurrentSettings(currentSettings);
  }

  localMenuGalleryImages.forEach((item) => {
    if (item?.type === 'local' && item.previewUrl) {
      try {
        URL.revokeObjectURL(item.previewUrl);
      } catch (_) {
        /* ignore */
      }
    }
  });
  localMenuGalleryImages = toSave.menuImages.map((url) => ({ type: 'remote', url }));
  setMenuModuleDirtyState(false);
  refreshAdminSummaries();

  const { applyClientUI } = await import('./slideshow.js?v=82.86');
  applyClientUI();

  if (!quiet) {
    showSnackbar('Modul meniu salvat.', 'success');
  }

  setTimeout(() => {
    import('../services/settings.js?v=82.86')
      .then((m) => m.loadSettings(uid, true))
      .catch((err) => {
        logger.warn('[saveMenuModuleSettings] Background reload failed:', err);
      });
  }, 3000);
}
